import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import type { Extensions } from '@tiptap/react';
import type { CRMUser } from '../types';
import {
  CommentMentionExtension,
  buildMentionSuggestion,
} from '../components/pm/RichCommentEditor';
import { commentListExtensions, commentListStarterKitOptions } from './richCommentListEditor';

/** Shared TipTap extensions for board notes. Pass getMentionableUsers to enable @mentions. */
export function createBoardNoteExtensions(
  placeholder = 'Double-click to edit',
  getMentionableUsers?: () => CRMUser[],
): Extensions {
  const extensions: Extensions = [
    StarterKit.configure({
      heading: { levels: [3] },
      ...commentListStarterKitOptions,
      codeBlock: { HTMLAttributes: { class: 'rce-codeblock' } },
      blockquote: { HTMLAttributes: { class: 'rce-blockquote' } },
      link: {
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank', class: 'rce-link' },
      },
    }),
    ...commentListExtensions(),
    Highlight.configure({ multicolor: false, HTMLAttributes: { class: 'rce-highlight' } }),
    Placeholder.configure({ placeholder }),
  ];

  if (getMentionableUsers) {
    extensions.push(
      CommentMentionExtension.configure({
        HTMLAttributes: { class: 'rce-mention', 'data-mention': 'true' },
        renderText({ node }) {
          const label = (node.attrs.label as string | null) || (node.attrs.id as string | null) || '';
          return `@${label}`;
        },
        renderHTML({ options, node }) {
          const label = (node.attrs.label as string | null) || (node.attrs.id as string | null) || '';
          return [
            'span',
            {
              ...(options.HTMLAttributes as Record<string, string>),
              'data-user-id': (node.attrs.id as string | null) ?? '',
              'data-label': label,
            },
            `@${label}`,
          ];
        },
        suggestion: buildMentionSuggestion(getMentionableUsers),
      }),
    );
  }

  return extensions;
}
